import { ColumnDef } from '@tanstack/react-table';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import PratusTable from 'components/DesignSystems/PratusTable/PratusTable';
import { useUpsertIncidentStatusSummary } from 'components/IAP/hooks/incidentStatusSummaryHooks';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { NotableItem } from 'types/iapBaseTypes';

const APPENDED_ROW_ID = 'APPENDED_ROW_ID';

const headerCell = (label: string) => (
  <span className="text-nowrap">{label}</span>
);

const NotableItemsTable = ({
  statusSummaryId,
  originalNotableItems = [],
}: {
  statusSummaryId: string;
  originalNotableItems?: NotableItem[];
}) => {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [rowBeingEditedId, setRowBeingEditedId] = useState<string | null>(null);
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  const form = useForm({
    mode: 'onChange',
    defaultValues: { notableItems: originalNotableItems },
  });

  const { control, register, getValues } = form;

  const { append, remove, update, fields } = useFieldArray<{
    notableItems: NotableItem[];
  }>({
    control,
    name: 'notableItems',
  });

  const rows = useMemo(() => {
    const filteredFields = fields.filter((field) =>
      field.item.toLowerCase().includes(search.toLowerCase())
    );

    const finalField = fields[fields.length - 1];

    if (
      finalField &&
      !filteredFields.some((field) => field.id === finalField.id) &&
      rowBeingEditedId === APPENDED_ROW_ID
    ) {
      return filteredFields.concat(finalField);
    }

    return filteredFields;
  }, [fields, search, rowBeingEditedId]);

  const allSelected = useMemo(() => {
    if (!rows.length) return false;
    return rows.length === selectedIds.length;
  }, [rows, selectedIds]);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(allSelected ? [] : rows.map((field) => field.id));
  }, [allSelected, rows]);

  const toggleRow = useCallback(
    (id: string) => {
      const set = new Set(selectedIds);
      if (set.has(id)) {
        set.delete(id);
      } else {
        set.add(id);
      }
      setSelectedIds([...set]);
    },
    [selectedIds]
  );

  const deleteSelected = useCallback(() => {
    const toDeleteIndexes = fields.reduce<number[]>((acc, field, index) => {
      if (selectedIds.includes(field.id)) {
        acc.push(index);
      }
      return acc;
    }, []);

    upsertIncidentStatusSummary({
      notableItems: getValues('notableItems').filter(
        (_: NotableItem, index: number) => !toDeleteIndexes.includes(index)
      ) as NotableItem[],
    });
    remove(toDeleteIndexes);
    setSelectedIds([]);
  }, [remove, fields, selectedIds, upsertIncidentStatusSummary, getValues]);

  const isRowEditable = useCallback(
    (rowId: string, rowIndex: number): boolean => {
      return (
        rowBeingEditedId === rowId ||
        (rowBeingEditedId === APPENDED_ROW_ID && rowIndex === rows.length - 1)
      );
    },
    [rowBeingEditedId, rows.length]
  );

  const deleteRow = useCallback(
    (rowToDelete: NotableItem & { id: string }) => {
      const index = fields.findIndex((field) => field.id === rowToDelete.id);
      if (index !== -1) {
        remove(index);
      }
      setSelectedIds((prev) => prev.filter((x) => x !== rowToDelete.id));
      upsertIncidentStatusSummary({
        notableItems: getValues('notableItems').filter(
          (_: NotableItem, idx: number) => idx !== index
        ) as NotableItem[],
      });
    },
    [remove, fields, upsertIncidentStatusSummary, getValues]
  );

  const appendBlankNotableItem = useCallback(() => {
    append({ item: '', numberThisReportingPeriod: '', totalNumberToDate: '' });
    setRowBeingEditedId(APPENDED_ROW_ID);
  }, [append, setRowBeingEditedId]);

  const columns = useMemo<ColumnDef<NotableItem & { id: string }>[]>(
    () => [
      {
        id: 'select',
        header: () => (
          <StylishNewCheckbox
            checked={allSelected}
            onChange={toggleSelectAll}
            disabled={rows.length === 0 || rowBeingEditedId !== null}
          />
        ),
        cell: ({ row }) => (
          <StylishNewCheckbox
            checked={selectedIds.includes(row.original.id)}
            onChange={() => {
              toggleRow(row.original.id);
            }}
            disabled={rowBeingEditedId !== null}
          />
        ),
        size: 10,
        enableSorting: false,
        meta: { headerAlign: 'left' },
      },
      {
        accessorKey: 'item',
        header: () => headerCell('Notable Item'),
        cell: ({ row }) => {
          if (isRowEditable(row.original.id, row.index)) {
            const fieldIndex = fields.findIndex(
              (field) => field.id === row.original.id
            );

            return (
              <StylishNewInput
                className="form-control"
                placeholder="Enter notable item"
                defaultValue={row.original.item}
                {...register(`notableItems.${fieldIndex}.item`)}
              />
            );
          } else {
            return <div>{row.original.item}</div>;
          }
        },
        enableSorting: true,
        enableResizing: false,
        size: 300,
        meta: { headerAlign: 'left' },
      },
      {
        accessorKey: 'totalThisPeriod',
        header: () => headerCell('Total This Reporting Period'),
        cell: ({ row }) => {
          if (isRowEditable(row.original.id, row.index)) {
            const fieldIndex = fields.findIndex(
              (field) => field.id === row.original.id
            );

            return (
              <StylishNewInput
                className="form-control"
                placeholder="Enter total this reporting period"
                defaultValue={row.original.numberThisReportingPeriod}
                {...register(
                  `notableItems.${fieldIndex}.numberThisReportingPeriod`
                )}
              />
            );
          } else {
            return <div>{row.original.numberThisReportingPeriod}</div>;
          }
        },
        enableSorting: true,
        size: 200,
        meta: { headerAlign: 'left' },
      },
      {
        accessorKey: 'totalToDate',
        header: () => headerCell('Total To Date'),
        cell: ({ row }) => {
          if (isRowEditable(row.original.id, row.index)) {
            const fieldIndex = fields.findIndex(
              (field) => field.id === row.original.id
            );

            return (
              <StylishNewInput
                className="form-control"
                placeholder="Enter total to date"
                defaultValue={row.original.totalNumberToDate}
                {...register(`notableItems.${fieldIndex}.totalNumberToDate`)}
              />
            );
          } else {
            return <div>{row.original.totalNumberToDate}</div>;
          }
        },
        enableSorting: true,
        size: 150,
        meta: { headerAlign: 'left' },
      },
      {
        id: 'actions',
        header: () => <span />,
        cell: ({ row }) => {
          if (isRowEditable(row.original.id, row.index)) {
            const fieldIndex = fields.findIndex(
              (field) => field.id === row.original.id
            );

            return (
              <div className="d-flex gap-1 justify-content-end">
                <StylishNewButton
                  className="button--secondary border-0"
                  onClick={(e) => {
                    e.stopPropagation();

                    const currentValues = getValues('notableItems');
                    upsertIncidentStatusSummary({
                      notableItems: currentValues,
                    });
                    update(fieldIndex, {
                      item: currentValues[fieldIndex].item,
                      numberThisReportingPeriod:
                        currentValues[fieldIndex].numberThisReportingPeriod,
                      totalNumberToDate:
                        currentValues[fieldIndex].totalNumberToDate,
                    });
                    setRowBeingEditedId(null);
                  }}
                >
                  <SharedIcon iconName="check" classes="me-1" />
                </StylishNewButton>
                <StylishNewButton
                  className="button--secondary border-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    update(fieldIndex, {
                      item: row.original.item,
                      numberThisReportingPeriod:
                        row.original.numberThisReportingPeriod,
                      totalNumberToDate: row.original.totalNumberToDate,
                    });
                    setRowBeingEditedId(null);
                  }}
                >
                  <SharedIcon iconName="cancel" classes="me-1" />
                </StylishNewButton>
              </div>
            );
          } else {
            return (
              <div className="d-flex gap-1 justify-content-end">
                <StylishNewButton
                  className="button--secondary border-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedIds([]);
                    setRowBeingEditedId(row.original.id);
                  }}
                  disabled={rowBeingEditedId !== null}
                >
                  <SharedIcon iconName="edit" classes="me-1" />
                </StylishNewButton>
                <StylishNewButton
                  className="button--secondary border-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteRow(row.original);
                  }}
                  disabled={rowBeingEditedId !== null}
                >
                  <SharedIcon iconName="delete" classes="me-1" />
                </StylishNewButton>
              </div>
            );
          }
        },
        enableSorting: false,
        size: 160,

        meta: { headerAlign: 'left' },
      },
    ],
    [
      allSelected,
      selectedIds,
      toggleRow,
      toggleSelectAll,
      register,
      deleteRow,
      rowBeingEditedId,
      setRowBeingEditedId,
      isRowEditable,
      update,
      getValues,
      upsertIncidentStatusSummary,
      fields,
      rows.length,
    ]
  );

  return (
    <>
      <FormProvider {...form}>
        <div className="d-flex flex-column justify-content-between flex-grow-1">
          <div className="d-flex justify-content-between align-items-end mb-2 gap-2 flex-wrap">
            <div
              className="flex-grow-1 justify-content-between align-items-center gap-2 d-flex"
              style={{ minWidth: 240 }}
            >
              <label style={{ whiteSpace: 'nowrap' }}>Notable Items</label>
              <StylishNewInput
                className="form-control"
                placeholder="Search notable items..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div style={{ paddingLeft: '4rem' }}>
              <StylishNewButton
                className="button button--primary button--sml"
                onClick={(e) => {
                  e.stopPropagation();
                  appendBlankNotableItem();
                }}
                disabled={rowBeingEditedId !== null}
              >
                <SharedIcon iconName="add" classes="me-2" />
                Add Notable Item
              </StylishNewButton>
            </div>
          </div>

          {selectedIds.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>{selectedIds.length} selected</div>
              <StylishNewButton
                className="button--error button--sml"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteSelected();
                }}
              >
                <SharedIcon iconName="delete" classes="me-1" /> Delete Selected
              </StylishNewButton>
            </div>
          )}
          <PratusTable
            className="layout-auto pratus-table"
            columns={columns}
            data={rows}
            getRowId={(row) => row.id}
            noDataIndication={
              <div className="text-center p-2">
                {search
                  ? 'No results!'
                  : 'There are no notable items yet. Click + Add Notable Item to add a notable item.'}
              </div>
            }
            footer={
              <span className="text-nowrap me-2">{`${rows.length} notable items`}</span>
            }
          />
        </div>
      </FormProvider>
    </>
  );
};

export default NotableItemsTable;
